import { Component, OnInit, computed, signal } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import {
  AccessRequest,
  CreatedInvitation,
  InvitableRole,
  OrganizationInvitation,
  OrganizationMember,
  OrganizationRole,
} from 'src/app/models/membership.model';
import { Team, TeamRef } from 'src/app/models/team.model';
import { AuthService } from 'src/app/services/auth.service';
import { MembershipService } from 'src/app/services/membership.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-organization',
  templateUrl: './organization.component.html',
  styleUrls: ['./organization.component.scss'],
  standalone: false,
})
export class OrganizationPageComponent implements OnInit {
  // Signals: the app does not repaint after async work on its own.
  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly members = signal<OrganizationMember[]>([]);
  readonly invitations = signal<OrganizationInvitation[]>([]);
  readonly accessRequests = signal<AccessRequest[]>([]);

  readonly roleLabels: Record<OrganizationRole, string> = { owner: 'Owner', admin: 'Admin', member: 'Member' };

  /** Your own role, from the members list. */
  readonly myRole = computed<OrganizationRole>(() => this.members().find((m) => m.is_you)?.role ?? 'member');
  readonly canManage = computed(() => this.myRole() === 'owner' || this.myRole() === 'admin');
  readonly isOwner = computed(() => this.myRole() === 'owner');

  readonly inviteEmail = signal('');
  readonly inviteRole = signal<InvitableRole>('member');
  readonly teams = signal<Team[]>([]);
  readonly inviteTeams = signal<Set<string>>(new Set());
  readonly inviting = signal(false);
  readonly inviteError = signal<string | null>(null);
  /** The link of the invitation just sent, shown once so it can be shared another way. */
  readonly lastInvite = signal<CreatedInvitation | null>(null);

  readonly busy = signal<string | null>(null);
  readonly confirmingRemove = signal<string | null>(null);
  readonly confirmingTransfer = signal<string | null>(null);
  readonly copied = signal(false);

  readonly organizationName = signal('');
  readonly inviteEmailValid = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.inviteEmail().trim()));

  constructor(
    private membership: MembershipService,
    private toast: ToastService,
    authService: AuthService,
  ) {
    authService.decodedAccessToken$.subscribe((token: any) => this.organizationName.set(token?.organization_name ?? ''));
  }

  async ngOnInit() {
    try {
      this.members.set(await lastValueFrom(this.membership.getMembers()));
      // Invitations and requests are for the owner and admins only.
      if (this.canManage()) {
        const [invitations, accessRequests, teams] = await Promise.all([
          lastValueFrom(this.membership.getInvitations()),
          lastValueFrom(this.membership.getAccessRequests()),
          lastValueFrom(this.membership.getTeams()),
        ]);
        this.invitations.set(invitations);
        this.accessRequests.set(accessRequests);
        this.teams.set(teams);
      }
    } catch {
      this.loadError.set(true);
    }
    this.loading.set(false);
  }

  initials(member: { first_name?: string; last_name?: string; email?: string; name?: string }): string {
    const name = member.name ?? `${member.first_name ?? ''} ${member.last_name ?? ''}`;
    const parts = name.trim().split(/\s+/).filter(Boolean);
    const letters = parts.length > 1 ? parts[0][0] + parts[1][0] : (parts[0] ?? member.email ?? '?').slice(0, 2);
    return letters.toUpperCase();
  }

  /** The owner removes anyone else; admins remove members only. */
  canRemove(member: OrganizationMember): boolean {
    if (member.is_you || member.role === 'owner') return false;
    return this.isOwner() || (this.myRole() === 'admin' && member.role === 'member');
  }

  async changeRole(member: OrganizationMember, role: InvitableRole) {
    if (role === member.role) return;
    this.busy.set(member.uuid);
    try {
      const updated = await lastValueFrom(this.membership.changeRole(member.uuid, role));
      this.members.update((list) => list.map((m) => (m.uuid === updated.uuid ? { ...m, role: updated.role } : m)));
      this.toast.show(`${member.first_name || member.email} is now ${this.roleLabels[updated.role].toLowerCase()}`, 'success');
    } catch (err: any) {
      this.toast.show(err?.detail ?? 'Could not change the role', 'error');
    } finally {
      this.busy.set(null);
    }
  }

  async transferOwnership(member: OrganizationMember) {
    this.busy.set(member.uuid);
    try {
      const changed = await lastValueFrom(this.membership.transferOwnership(member.uuid));
      const roles = new Map(changed.map((m) => [m.uuid, m.role]));
      this.members.update((list) => list.map((m) => ({ ...m, role: roles.get(m.uuid) ?? m.role })));
      this.toast.show(`${member.first_name || member.email} now owns the organization`, 'success');
    } catch (err: any) {
      this.toast.show(err?.detail ?? 'Could not transfer ownership', 'error');
    } finally {
      this.busy.set(null);
      this.confirmingTransfer.set(null);
    }
  }

  toggleInviteTeam(uuid: string) {
    this.inviteTeams.update((set) => {
      const next = new Set(set);
      next.has(uuid) ? next.delete(uuid) : next.add(uuid);
      return next;
    });
  }

  teamNames(teams: TeamRef[]): string {
    return teams.map((t) => t.name).join(', ');
  }

  async invite(
    email = this.inviteEmail().trim(),
    role: InvitableRole = this.inviteRole(),
    teamUuids: string[] = [...this.inviteTeams()],
  ) {
    if (!email || this.inviting()) return;
    this.inviting.set(true);
    this.inviteError.set(null);
    try {
      const created = await lastValueFrom(this.membership.invite(email, role, teamUuids));
      this.showCreated(created);
      this.inviteEmail.set('');
      this.inviteTeams.set(new Set());
    } catch (err: any) {
      // The error interceptor rethrows the response body.
      const message = err?.email?.[0] ?? err?.detail ?? err?.error?.email?.[0];
      this.inviteError.set(message ?? 'Could not send the invitation.');
    } finally {
      this.inviting.set(false);
    }
  }

  async resend(invitation: OrganizationInvitation) {
    this.busy.set(invitation.uuid);
    await this.invite(invitation.email, invitation.role, (invitation.teams ?? []).map((t) => t.uuid));
    this.busy.set(null);
  }

  async revoke(invitation: OrganizationInvitation) {
    this.busy.set(invitation.uuid);
    try {
      await lastValueFrom(this.membership.revokeInvitation(invitation.uuid));
      this.invitations.update((list) => list.filter((i) => i.uuid !== invitation.uuid));
      if (this.lastInvite()?.uuid === invitation.uuid) this.lastInvite.set(null);
      this.toast.show('Invitation revoked', 'success');
    } catch {
      this.toast.show('Could not revoke the invitation', 'error');
    } finally {
      this.busy.set(null);
    }
  }

  async remove(member: OrganizationMember) {
    this.busy.set(member.uuid);
    try {
      await lastValueFrom(this.membership.removeMember(member.uuid));
      this.members.update((list) => list.filter((m) => m.uuid !== member.uuid));
      this.toast.show(`${member.first_name || member.email} was removed`, 'success');
    } catch (err: any) {
      this.toast.show(err?.detail ?? 'Could not remove the member', 'error');
    } finally {
      this.busy.set(null);
      this.confirmingRemove.set(null);
    }
  }

  async approve(request: AccessRequest) {
    this.busy.set(request.uuid);
    try {
      // Straight into the teams that own the project they wanted to open.
      const teamUuids = request.project?.teams?.map((t) => t.uuid) ?? [];
      const created = await lastValueFrom(this.membership.approveAccessRequest(request.uuid, 'member', teamUuids));
      this.accessRequests.update((list) => list.filter((r) => r.uuid !== request.uuid));
      this.showCreated(created);
    } catch (err: any) {
      this.toast.show(err?.email?.[0] ?? err?.detail ?? 'Could not approve the request', 'error');
    } finally {
      this.busy.set(null);
    }
  }

  async dismiss(request: AccessRequest) {
    this.busy.set(request.uuid);
    try {
      await lastValueFrom(this.membership.dismissAccessRequest(request.uuid));
      this.accessRequests.update((list) => list.filter((r) => r.uuid !== request.uuid));
    } catch {
      this.toast.show('Could not dismiss the request', 'error');
    } finally {
      this.busy.set(null);
    }
  }

  async copyLink() {
    const url = this.lastInvite()?.invite_url;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2000);
    } catch {
      this.toast.show('Couldn’t copy — select the link and copy it manually', 'error');
    }
  }

  private showCreated(created: CreatedInvitation) {
    const { invite_url, email_sent, ...listed } = created;
    this.invitations.update((list) => [listed, ...list.filter((i) => i.email !== created.email)]);
    this.lastInvite.set(created);
    this.copied.set(false);
    this.toast.show(email_sent ? `Invitation sent to ${created.email}` : 'Invitation created — share the link', 'success');
  }
}
